import { starvedBlockCauses, deriveGraph, flowLadderOrder, flowSlotId, isExitAction, taskConnections, type FlowSlot, type RefCensusTask, type TaskLink } from '@agavi/blocks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Block } from '@/types/experience';

import { useCapability } from '@/hooks/use-capabilities';
import { exitSlotTitle } from '@/lib/flow-slot-title';
import { flowIsAuthored, noSensorDataBlocks, poorSensorDataBlocks } from '@/lib/flow-utils';

/**
 * Where this task sits: who can send a student here, and where they can go next.
 *
 * A DRAWING rather than two lists, because the thing a teacher is trying to hold in their head is
 * a shape — one task with arrows in and out — and a list makes them rebuild it every time. The
 * arrows carry the direction; the rows carry the names.
 *
 * Inline SVG at a fixed `viewBox` scaled to the panel, so the geometry is computed once here in
 * diagram units and the browser does the fitting. Text cannot wrap in SVG, so names are clipped to
 * a character budget rather than allowed to run over the arrows.
 */

/**
 * The one row a collapsed stay band draws. Identity, not shape, is what the predicates test.
 *
 * It is labelled with the TASK'S OWN NAME rather than a rung's. "Default" and "Anything Else"
 * both name a position in a ladder this teacher cannot see and has never been told about — the
 * owner's words: *"if you don't know flow, you have no idea why it would say 'Anything Else'
 * there"*. The task's name says the same thing in the only vocabulary the drawing has already
 * taught: the arrows leave the card, and this row is the one that does not.
 *
 * It does not duplicate a heading, either: the card deliberately draws no title of its own,
 * because the panel above carries it — so inside the card the name is new information.
 */
const COLLAPSED_STAY: FlowSlot = { kind: 'default' };

const W = 360;
const ROW_H = 34;
const ROW_GAP = 5;
const LABEL_H = 16;
const BAND_GAP = 20;

/**
 * The task card holds its STAY rungs, so its height follows how many there are.
 *
 * It used to be a fixed 68 saying "THIS TASK" over the task's own title — a name the panel's
 * heading, the board and the editor were all already showing. What the card is for is the place
 * a student does not leave, so what belongs inside it is every rung that keeps them there.
 */
const RUNG_H = 21;
const RUNG_GAP = 3;
const BLOCK_PAD = 13;

/** Rows sit between the two buses: arrivals join on the right, exits are fed from the left. */
const ROW_X = 26;
const ROW_W = 308;
const IN_BUS_X = 350;
const OUT_BUS_X = 10;
const BLOCK_X = 52;
const BLOCK_W = 282;
const BLOCK_MID = BLOCK_X + BLOCK_W / 2;

/**
 * Clipped, not wrapped.
 *
 * An over-long name is unusual and a clipped one still identifies the row; a name allowed to run
 * is a name drawn across the arrows, which breaks the only thing the picture is for.
 */
function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function PreviewConnectionsDiagram({ tasks, taskId, arrival, firedSlot, predictedSlot }: {
  tasks: readonly RefCensusTask[];
  taskId: string;
  /**
   * The route the student actually took in, if any. Marked rather than filtered: the other ways
   * in are still what the teacher is checking, and hiding them would turn a map into a receipt.
   */
  arrival?: TaskLink | null;
  /** The exit this task last left by. Null while the student is still on it. */
  firedSlot?: FlowSlot | null;
  /**
   * The exit the answer currently in the boxes WOULD take, from the real ladder.
   *
   * Drawn dashed where a fired exit is solid, because the two are different claims: one happened,
   * the other is a forecast that changes with the next keystroke. Never both at once -- once an
   * exit has fired, that is the answer.
   */
  predictedSlot?: FlowSlot | null;
}) {
  const { t } = useTranslation();
  const { arrivesFrom, goesTo } = taskConnections(tasks, taskId);
  const byId = new Map(tasks.map(x => [x.id, x]));
  const titleOf = (id: string) => byId.get(id)?.title?.trim() || t('task.flow.rule.unknownBlock');
  const slotNameOf = (link: TaskLink) => {
    const flow = byId.get(link.ownerTaskId)?.flow;
    return flow ? exitSlotTitle(link.slot, flow, t) : flowSlotId(link.slot);
  };

  /** Where a link lands, in words. An ending and an undecided goto must not read the same. */
  const destOf = (link: TaskLink) => (
    link.target.kind === 'task' ? titleOf(link.target.taskId)
      : link.target.kind === 'end' ? t('task.bottomBar.debugEndsHere')
        : t('task.bottomBar.debugNotChosen')
  );

  // An empty band still draws one row, saying so. A band that simply vanished would leave the
  // teacher unsure whether the task has no arrivals or the panel failed to look.
  const inRows = arrivesFrom.length ? arrivesFrom : [null];

  /**
   * INSIDE the card, the rungs that keep the student here; BELOW it, the ones that do not.
   *
   * Both halves come from the same ordered walk, so a rung is in exactly one of them and the
   * two together are the whole ladder. The order is the order the engine tries them, which is
   * the only thing that explains why a rule a teacher wrote never seems to fire.
   */
  const flow = byId.get(taskId)?.flow;

  /**
   * A SENSOR RUNG NO BLOCK CAN REACH IS NOT DRAWN, on either half.
   *
   * The same gate the Flow panel uses -- `FlowSensorRung` returns null on an empty `sensing`,
   * fed by these two -- so the rung a teacher cannot see or edit over there is not a line they
   * have to read over here. Asked of what a block can FIRE rather than its type, so a GPS block
   * with its accuracy gate switched off suppresses the rung exactly as a task with no sensor at
   * all does, and nothing here needs updating when a block type grows a sensor state.
   *
   * It can never fire either: `resolveFlowOutcome` only takes a sensor rung when some block
   * reports that trigger. So this hides noise, not a route.
   *
   * AND NEITHER IS AN ALL CORRECT NOTHING REACHES, for the same reason one rung up.
   *
   * A block whose pass state no answer can produce starves the rung: `resolveFlowOutcome` can
   * never take it, so the line out of it is a route no student travels. The Flow panel marks
   * that card and dims its cyclers, because over there the teacher can go and repair the block.
   * Here there is nothing to repair and nothing to read — the drawing is a picture of where
   * students go, and a branch nobody takes is a branch that makes the picture wrong.
   *
   * Asked of the BLOCKS, which is where the fault is, rather than of the flow config: the rung
   * is authored and perfectly valid, and what makes it dead is somewhere else entirely.
   */
  const blocks = (byId.get(taskId)?.blocks ?? []) as Block[];
  const reachable = (slot: FlowSlot) => (
    slot.kind === 'noSensorData' ? noSensorDataBlocks(blocks).length > 0
      : slot.kind === 'poorSensorData' ? poorSensorDataBlocks(blocks).length > 0
        : slot.kind === 'allCorrect' ? starvedBlockCauses(blocks).length === 0
          : true
  );

  const ladder = flow ? flowLadderOrder(flow).filter(r => reachable(r.slot)) : [];
  const allStays = ladder.filter(r => !isExitAction(r.action)).map(r => r.slot);

  /*
   * ONE STAY ROW FOR A TEACHER WHO CANNOT AUTHOR THE LADDER.
   *
   * Unauthored, every rung above All Correct resolves to `stay`, so the band lists Any Incomplete,
   * both sensor rungs and Default as four separate lines that all mean the same thing and none of
   * which can be changed from here. For a beginner that is four pieces of vocabulary in place of
   * the one fact the drawing is for: finish it and you go on, otherwise you stay.
   *
   * It collapses the DRAWING and nothing else — the engine still walks every rung, and the
   * collapsed row lights whenever any of them took the student, so the diagram keeps answering
   * the question it exists to answer. The ladder comes back whole the moment Flow is on, or the
   * moment this task's routing is authored, which is `flowIsAuthored`'s job either way.
   */
  // `deriveGraph().nextOf` is the same successor the Flow panel asks about, so the drawing and
  // the panel cannot disagree over whether this task's routing was authored.
  const throughLine = useMemo(
    () => deriveGraph([...tasks] as Parameters<typeof deriveGraph>[0]).nextOf[taskId] ?? null,
    [tasks, taskId]);
  const collapseStays = !useCapability('flow') && !(flow ? flowIsAuthored(flow, throughLine) : false);
  // COPIED, not aliased: the `push` below is a mutation, and handing it `allStays` itself put a
  // null into the list `stayIds` maps over — which only showed up on a task with no stay rungs at
  // all, because that is the only shape where the push fires.
  const stayRows: (FlowSlot | null)[] = collapseStays && allStays.length > 0
    ? [COLLAPSED_STAY]
    : [...allStays];
  if (!stayRows.length) stayRows.push(null);

  /** Which slots the collapsed row stands in for, so it lights when any of them did. */
  const stayIds = new Set(allStays.map(flowSlotId));

  /* A split sensor rung fans out to per-block slots that the walk does not list, so the fallback
     sorts them with their own rung rather than off the end. */
  const rank = new Map(ladder.map((r, i) => [flowSlotId(r.slot), i]));
  const rankOf = (slot: FlowSlot) => rank.get(flowSlotId(slot)) ?? rank.get(slot.kind) ?? ladder.length;
  const exits = goesTo.filter(l => reachable(l.slot)).sort((a, b) => rankOf(a.slot) - rankOf(b.slot));
  const outRows = exits.length ? exits : [null];

  const rowY = (top: number, i: number) => top + i * (ROW_H + ROW_GAP);
  const inTop = LABEL_H;
  const inEnd = rowY(inTop, inRows.length) - ROW_GAP;
  const blockY = inEnd + BAND_GAP;
  const blockH = BLOCK_PAD * 2 + stayRows.length * RUNG_H + (stayRows.length - 1) * RUNG_GAP;
  const outLabelY = blockY + blockH + BAND_GAP;
  const outTop = outLabelY + 6;
  const H = rowY(outTop, outRows.length) - ROW_GAP + 4;

  const mid = (top: number, i: number) => rowY(top, i) + ROW_H / 2;
  const lastInMid = mid(inTop, 0);
  const lastOutMid = mid(outTop, outRows.length - 1);

  /**
   * EACH BAND'S LABEL SITS AWAY FROM ITS OWN BUS.
   *
   * Arrivals leave from the rows' right edge and join a trunk on the right, so their label is
   * clear at the left. Exits are fed from the left, and the trunk's elbow passes exactly where a
   * left-aligned label would be -- it overlapped the arrow. So GOES TO goes to the other side.
   */
  const label = (y: number, text: string, align: 'start' | 'end' = 'start') => (
    <text
      x={align === 'end' ? ROW_X + ROW_W : ROW_X} y={y} textAnchor={align}
      fontSize={10.5} fontWeight={700} letterSpacing='0.06em' className='fill-ws-muted'
    >{text}</text>
  );

  /**
   * EACH BAND READS IN ITS OWN DIRECTION, and they are not mirror images.
   *
   * An arrival names the task you came FROM and the rung that sent you; an exit names the rung you
   * leave BY and where it lands. Rendering both as "slot, then target" put this task's own name in
   * its own arrivals list — it was the target of every one of them — which says nothing. The bug
   * survived a green test because the fixture happened to use the same title in the other band.
   */
  const sameLink = (a: TaskLink, b: TaskLink) =>
    a.ownerTaskId === b.ownerTaskId && flowSlotId(a.slot) === flowSlotId(b.slot);

  const isPredicted = (slot: FlowSlot) =>
    !!predictedSlot && flowSlotId(predictedSlot) === flowSlotId(slot);
  const isFired = (slot: FlowSlot) =>
    !!firedSlot && flowSlotId(firedSlot) === flowSlotId(slot);

  /* A stay row asks the same question of ONE rung or of all of them, depending on whether the
     band was collapsed above. Separate from `isFired`/`isPredicted` because the exits below use
     those unchanged — an exit is never collapsed, there being only ever one route out per rung. */
  const stayFired = (slot: FlowSlot) => (slot === COLLAPSED_STAY
    ? !!firedSlot && stayIds.has(flowSlotId(firedSlot))
    : isFired(slot));
  const stayPredicted = (slot: FlowSlot) => (slot === COLLAPSED_STAY
    ? !!predictedSlot && stayIds.has(flowSlotId(predictedSlot))
    : isPredicted(slot));

  /* Whether the route the student is on ends INSIDE this card. Asked of the stay rungs rather
     than of the exits, because "no exit lit" is also true before anyone has answered anything,
     and the card has to stay clear then. */
  const stayLit = stayRows.some(slot => !!slot && (stayFired(slot) || stayPredicted(slot)));

  /**
   * THE GLYPH IS THE ANSWER, so only the rung that is firing gets one.
   *
   * A square where the student stops, a play arrow where they go -- one mark on the whole
   * drawing, and the shape says which of the two happened. Filled for what fired, outlined for
   * what a draft answer only forecasts: the outline is the non-colour half of that signal, the
   * same split the row strokes use, and it survives a colour-blind read.
   */
  const glyphClass = (live: boolean, _forecast: boolean) => (
    live ? 'fill-ws-primary stroke-ws-primary' : 'fill-transparent stroke-ws-primary'
  );

  const row = (left: string | null, right: string, y: number, live = false, forecast = false, go = false) => (
    <g key={y} data-row=''>
      <rect
        x={ROW_X} y={y} width={ROW_W} height={ROW_H} rx={8}
        className={left === null ? 'fill-transparent stroke-ws-border'
          : live || forecast ? 'fill-ws-primary-subtle stroke-ws-primary' : 'fill-ws-surface stroke-ws-muted'}
        /* Dashed for a forecast, solid for what happened: one is a claim about the past and the
           other changes with the next keystroke, and a teacher must be able to tell them apart
           without remembering which colour means which. */
        /* ONE WIDTH, whatever the state. A row that thickened on highlight grew by a quarter
           point each side, which reads as the row shifting rather than lighting up -- and the
           resting outline was so faint the card looked like floating text until it did. The
           non-colour half of the signal lives on the stub instead, which is free to change
           weight because nothing is boxed by it. */
        strokeWidth={1.5}
        /* Dashed twice over, for two different absences: an empty band has no rows to draw, and
           a forecast has not happened yet. */
        strokeDasharray={left === null ? '4 3' : forecast && !live ? '5 3' : undefined}
      />
      {/*
        AFTER the rect, because SVG paints in document order and the rect has a fill.
        Drawn before it, the arrow was in the DOM and under the card -- present to a test that
        asked whether it existed, invisible to the teacher it was for. Reported as "why no play
        arrow in front of the go, there seems to be space in it": the space was the indent the
        text leaves for a glyph that was being painted over.
        It goes on the exit TAKING the student, not on every exit: an arrow against each one was
        the band describing itself, and the question is which one.
      */}
      {go && left !== null && (live || forecast) && (
        <path
          data-glyph='go'
          d={`M${ROW_X + 12} ${y + ROW_H / 2 - 4.5} L${ROW_X + 20} ${y + ROW_H / 2} L${ROW_X + 12} ${y + ROW_H / 2 + 4.5} z`}
          strokeWidth={1.5} strokeLinejoin='round' className={glyphClass(live, forecast)}
        />
      )}
      {left === null ? (
        <text x={ROW_X + 12} y={y + ROW_H / 2 + 4.5} fontSize={12} className='fill-ws-muted'>{right}</text>
      ) : (
        <>
          {/* The arrow takes the first 14px of the row, so a name that keeps its old budget
              would run that much further right and into the "via" column. */}
          <text x={ROW_X + (go ? 26 : 12)} y={y + ROW_H / 2 + 4.5} fontSize={13} fontWeight={live || forecast ? 600 : undefined} className={live || forecast ? 'fill-ws-primary' : 'fill-ws-text'}>{clip(left, go ? 24 : 26)}</text>
          {/* "via" names what the second column IS. Without it a row read as two titles side by
              side, and which one was the rung depended on recognising the rung's name. */}
          <text x={ROW_X + ROW_W - 12} y={y + ROW_H / 2 + 4.5} fontSize={11} fontWeight={live || forecast ? 600 : undefined} textAnchor='end' className={live || forecast ? 'fill-ws-primary' : 'fill-ws-muted'}>
            {t('task.bottomBar.debugVia', { event: clip(right, 14) })}
          </text>
        </>
      )}
    </g>
  );

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`} width='100%' style={{ height: 'auto' }}
      role='img'
      // Each count pluralised on its own, because i18next gives a string one `count` and this
      // sentence carries two -- "reached by 1 routes" is the sort of thing a screen reader says
      // out loud.
      aria-label={t('task.bottomBar.debugConnectionsAlt', {
        task: titleOf(taskId),
        from: t('task.bottomBar.debugAltIn', { count: arrivesFrom.length }),
        to: t('task.bottomBar.debugAltOut', { count: goesTo.length }),
      })}
    >
      <defs>
        {/* `context-stroke` is not relied on: one marker per colour, each referenced explicitly, so
            the head is the same colour as its line in every browser we serve. */}
        <marker id='pcdHead' viewBox='0 0 10 10' refX={10} refY={5} markerWidth={5.5} markerHeight={5.5} orient='auto-start-reverse'>
          <path d='M0 0 L10 5 L0 10 z' className='fill-ws-muted' />
        </marker>
        <marker id='pcdHeadLive' viewBox='0 0 10 10' refX={10} refY={5} markerWidth={5.5} markerHeight={5.5} orient='auto-start-reverse'>
          <path d='M0 0 L10 5 L0 10 z' className='fill-ws-primary' />
        </marker>
      </defs>

      {label(11, t('task.bottomBar.debugArrivesFrom'))}
      {inRows.map((link, i) => (link
        ? row(titleOf(link.ownerTaskId), slotNameOf(link), rowY(inTop, i), !!arrival && sameLink(arrival, link))
        : row(null, t('task.bottomBar.debugNoArrivals'), rowY(inTop, i))))}
      {/* Each arrival stubs out to a shared trunk that drops into the task. The stubs are what
          differ per row; the trunk is shared because every one of them is the same arrival. */}
      {arrivesFrom.map((link, i) => {
        const live = !!arrival && sameLink(arrival, link);
        return (
          <path
            key={i} d={`M${ROW_X + ROW_W} ${mid(inTop, i)} H${IN_BUS_X}`} fill='none'
            strokeWidth={live ? 2 : 1.25} className={live ? 'stroke-ws-primary' : 'stroke-ws-muted'}
          />
        );
      })}
      {arrivesFrom.length > 0 && (
        <path
          d={`M${IN_BUS_X} ${lastInMid} V${blockY - 14} H${BLOCK_MID} V${blockY}`}
          fill='none' strokeWidth={1.25} markerEnd='url(#pcdHead)' className='stroke-ws-muted'
        />
      )}

      {/*
        THE CARD LIGHTS WHEN THE STUDENT IS STAYING IN IT, and not otherwise.
        It used to be filled always, which meant the one card that was certainly involved looked
        the same whether the submission ended here or left -- and a lit rung inside a lit card
        had nothing to stand out against. Clear is now the resting state: the card lights only
        when the rung that would fire is one of its own, which is the same claim the exit rows
        make for themselves when one of THEM is the route.
        One stroke width either way: a card that thickened on highlight read as the whole
        diagram shifting rather than as this card lighting up.
      */}
      <rect
        x={BLOCK_X} y={blockY} width={BLOCK_W} height={blockH} rx={11} strokeWidth={2}
        data-card={stayLit ? 'lit' : 'clear'}
        className={stayLit ? 'fill-ws-primary-subtle stroke-ws-primary' : 'fill-ws-surface stroke-ws-border'}
      />
      {/*
        NO NAME AND NO LABEL. The card's position says what it is -- every arrival points into it
        and every exit leaves it -- and the task's title is already on the panel heading above.
        What it holds instead is the rungs a student does not leave by.

        The marker carries the non-colour half of the signal, the way the rows' stroke does:
        filled for a rung that fired, hollow for one a draft answer only forecasts.
      */}
      {stayRows.map((slot, i) => {
        const y = blockY + BLOCK_PAD + i * (RUNG_H + RUNG_GAP);
        if (!slot) {
          return (
            /* "No events", not "No rungs stay here". A rung is the ladder's own word for its
               positions, and this band is empty precisely for a teacher who has never been
               shown that ladder — explaining the gap in the vocabulary they do not have is
               what made the line read as a fault rather than an ordinary empty state. */
            <text key='none' x={BLOCK_X + 14} y={y + 15} fontSize={12} className='fill-ws-muted'>
              {t('task.bottomBar.debugNoStays')}
            </text>
          );
        }
        const live = stayFired(slot);
        const forecast = stayPredicted(slot);
        return (
          <g key={flowSlotId(slot)} data-stay=''>
            {/* ONLY THE ONE THAT IS FIRING. A glyph on every rung said what each rung DOES,
                which the card and the band already say by where they sit -- and twelve of them
                left the one that mattered with nothing to stand out against. The text keeps its
                indent whether or not a glyph is drawn, so the list does not shuffle sideways as
                the answer changes. */}
            {(live || forecast) && (
              <rect
                data-glyph='stop'
                x={BLOCK_X + 14} y={y + RUNG_H / 2 - 3.5} width={7} height={7} rx={1} strokeWidth={1.5}
                className={glyphClass(live, forecast)}
              />
            )}
            <text
              x={BLOCK_X + 30} y={y + 15} fontSize={12.5}
              fontWeight={live || forecast ? 600 : undefined}
              className={live || forecast ? 'fill-ws-primary' : 'fill-ws-text'}
            >{clip(slot === COLLAPSED_STAY ? titleOf(taskId)
              : flow ? exitSlotTitle(slot, flow, t) : flowSlotId(slot), 30)}</text>
          </g>
        );
      })}

      {exits.length > 0 && (
        <path
          d={`M${BLOCK_MID} ${blockY + blockH} V${blockY + blockH + 14} H${OUT_BUS_X} V${lastOutMid}`}
          fill='none' strokeWidth={1.25} className='stroke-ws-muted'
        />
      )}
      {/*
        DESTINATION FIRST, event second -- the same shape as an arrival, so both bands read
        "which task, by which rung" rather than inverting between them. Where a student ENDS UP
        is the thing being checked; which rung took them there is the qualifier.

        NO BAND LABEL. The arrow arriving at each row already says these are exits, and the
        heading was one more thing competing with the names for a narrow panel's width.
      */}
      {outRows.map((link, i) => (link
        ? row(destOf(link), slotNameOf(link), rowY(outTop, i),
          !!firedSlot && flowSlotId(firedSlot) === flowSlotId(link.slot), isPredicted(link.slot), true)
        : row(null, t('task.bottomBar.debugNoExits'), rowY(outTop, i))))}
      {/* Fed from the LEFT so an exit reads slot-then-destination, the same direction as its text. */}
      {exits.map((link, i) => {
        const live = !!firedSlot && flowSlotId(firedSlot) === flowSlotId(link.slot);
        const forecast = isPredicted(link.slot);
        return (
          <path
            key={i} d={`M${OUT_BUS_X} ${mid(outTop, i)} H${ROW_X}`} fill='none'
            strokeWidth={live || forecast ? 2 : 1.25}
            strokeDasharray={forecast && !live ? '5 3' : undefined}
            markerEnd={live || forecast ? 'url(#pcdHeadLive)' : 'url(#pcdHead)'}
            className={live || forecast ? 'stroke-ws-primary' : 'stroke-ws-muted'}
          />
        );
      })}
    </svg>
  );
}
